import * as React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Send, Clock, CheckCircle, XCircle, Building2, User, Mail, Trash2 } from 'lucide-react'
import { ticketService, Ticket, TicketMessage } from '@/services/ticketService'
import { useToast } from '@/components/ui/use-toast'
import { Button } from '@/components/ui/button'
import { RichTextEditor } from '@/components/email/RichTextEditor'
import { useWebSocketContext } from '@/context/WebSocketContext'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { addMessageHandler, removeMessageHandler } = useWebSocketContext()

  const [ticket, setTicket] = React.useState<Ticket | null>(null)
  const [messages, setMessages] = React.useState<TicketMessage[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [newMessage, setNewMessage] = React.useState('')
  const [sending, setSending] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const messagesEndRef = React.useRef<HTMLDivElement>(null)

  // Ticket detayını getir
  const fetchTicket = React.useCallback(async () => {
    if (!id) return
    try {
      setIsLoading(true)
      const data = await ticketService.getTicketById(id)
      setTicket(data.ticket)
      setMessages(data.messages)
    } catch (error) {
      console.error('Ticket getirilemedi:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Ticket detayı yüklenemedi.'
      })
    } finally {
      setIsLoading(false)
    }
  }, [id, toast])

  React.useEffect(() => {
    fetchTicket()
  }, [fetchTicket])

  // WebSocket listeners for real-time updates
  React.useEffect(() => {
    if (!id) return

    const handleMessage = (message: any) => {
      // Yeni mesaj geldi - SADECE MÜŞTERİDEN GELEN MESAJLARI EKLE
      // (Admin'in kendi mesajı zaten API response'dan eklendi)
      if (message.type === 'ticket_message' &&
          message.data.ticketId === id &&
          message.data.sender.userType !== 'admin') {
        // WebSocket'ten gelen mesaj reconstruct et
        const newMessage: TicketMessage = {
          _id: message.data.messageId,
          ticket: message.data.ticketId,
          content: message.data.content,
          sender: message.data.sender,
          isSystemMessage: message.data.isSystemMessage || false,
          createdAt: message.data.createdAt,
          attachments: [],
          emailSent: false
        }
        // Duplicate kontrolü - aynı mesaj zaten varsa ekleme
        setMessages(prev => {
          if (prev.some(m => m._id === message.data.messageId)) {
            return prev
          }
          return [...prev, newMessage]
        })
        toast({
          title: 'Yeni Mesaj',
          description: 'Müşteri yeni bir mesaj gönderdi.'
        })
      }

      // Ticket durumu değişti
      if (message.type === 'ticket_status' && message.data.ticketId === id) {
        setTicket(prev => prev ? { ...prev, status: message.data.newStatus } : null)
      }
    }

    addMessageHandler(handleMessage)

    return () => {
      removeMessageHandler(handleMessage)
    }
  }, [addMessageHandler, removeMessageHandler, id, toast])

  // Scroll to bottom when messages change
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
  }, [messages])

  // Mesaj gönder
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !id || sending) return

    setSending(true)
    try {
      const message = await ticketService.addMessage(id, newMessage.trim())
      setMessages(prev => [...prev, message])
      setNewMessage('')
      toast({
        title: 'Başarılı',
        description: 'Mesaj gönderildi ve müşteriye email gönderildi.'
      })
    } catch (error) {
      console.error('Mesaj gönderilemedi:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Mesaj gönderilemedi.'
      })
    }
    setSending(false)
  }

  // Durum güncelle
  const handleStatusChange = async (newStatus: string) => {
    if (!id) return
    try {
      await ticketService.updateStatus(id, newStatus)
      setTicket(prev => prev ? { ...prev, status: newStatus as any } : null)
      toast({
        title: 'Başarılı',
        description: 'Ticket durumu güncellendi.'
      })
      // Refresh messages to get system message
      fetchTicket()
    } catch (error) {
      console.error('Durum güncellenemedi:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Durum güncellenemedi.'
      })
    }
  }

  // Ticket sil
  const handleDelete = async () => {
    if (!id) return
    setDeleting(true)
    try {
      await ticketService.deleteTicket(id)
      toast({
        title: 'Başarılı',
        description: 'Ticket silindi.'
      })
      navigate('/tickets')
    } catch (error) {
      console.error('Ticket silinemedi:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Ticket silinemedi.'
      })
    }
    setDeleting(false)
    setDeleteDialogOpen(false)
  }

  if (isLoading || !ticket) {
    return (
      <div className="h-full flex-1 p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 w-32 bg-gray-200 rounded" />
          <div className="h-48 bg-gray-200 rounded-xl" />
          <div className="h-96 bg-gray-200 rounded-xl" />
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex-1 flex flex-col">
      <div className="flex-1 p-8 pt-6 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Geri
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400 font-mono">#{ticket.ticketNumber}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${ticketService.getStatusColor(ticket.status)}`}>
                {ticketService.getStatusText(ticket.status)}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${ticketService.getPriorityColor(ticket.priority)}`}>
                {ticketService.getPriorityText(ticket.priority)}
              </span>
            </div>
            <h1 className="text-xl font-bold text-slate-900 mt-1">{ticket.subject}</h1>
          </div>
          <Select value={ticket.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Durum Değiştir" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Açık</SelectItem>
              <SelectItem value="in_progress">İşlemde</SelectItem>
              <SelectItem value="waiting_customer">Müşteri Bekleniyor</SelectItem>
              <SelectItem value="resolved">Çözüldü</SelectItem>
              <SelectItem value="closed">Kapatıldı</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex gap-6 overflow-hidden">
          {/* Messages Column */}
          <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Messages List */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => {
                const isAdmin = message.sender.userType === 'admin'
                const isSystem = message.isSystemMessage

                if (isSystem) {
                  return (
                    <div key={message._id} className="flex justify-center">
                      <span className="text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                        {message.content}
                      </span>
                    </div>
                  )
                }

                return (
                  <div key={message._id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] ${isAdmin ? 'order-1' : 'order-2'}`}>
                      <div className={`px-4 py-3 rounded-2xl ${
                        isAdmin
                          ? 'bg-emerald-500 text-white rounded-br-sm'
                          : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                      }`}>
                        <div
                          className="text-sm tiptap-content"
                          dangerouslySetInnerHTML={{ __html: message.content }}
                        />
                      </div>
                      <div className={`flex items-center gap-2 mt-1 text-xs text-slate-400 ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                        <span className="font-medium">{message.sender.name}</span>
                        <span>•</span>
                        <span>
                          {new Date(message.createdAt).toLocaleString('tr-TR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {isAdmin && message.emailSent && (
                          <>
                            <span>•</span>
                            <span className="text-emerald-600">Email gönderildi</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            {ticket.status !== 'closed' && (
              <div className="flex-shrink-0 p-4 border-t border-slate-200">
                <div className="space-y-3">
                  <div className="max-h-[300px] overflow-auto">
                    <RichTextEditor
                      content={newMessage}
                      onChange={setNewMessage}
                      placeholder="Yanıtınızı yazın... (Resim, link ve formatlamalar ekleyebilirsiniz)"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-400">
                      Mesajınız gönderildiğinde müşteriye otomatik email gönderilecektir.
                    </p>
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sending}
                      className="px-6 bg-emerald-500 hover:bg-emerald-600"
                    >
                      {sending ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Gönder
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info Sidebar */}
          <div className="w-80 space-y-4">
            {/* Customer Info */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Müşteri Bilgileri</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{ticket.createdBy.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{ticket.createdBy.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{ticket.company?.name || 'Bilinmeyen Firma'}</span>
                </div>
              </div>
            </div>

            {/* Ticket Info */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Ticket Bilgileri</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Kategori</span>
                  <span className="text-slate-600">{ticketService.getCategoryText(ticket.category)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Öncelik</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${ticketService.getPriorityColor(ticket.priority)}`}>
                    {ticketService.getPriorityText(ticket.priority)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Oluşturulma</span>
                  <span className="text-slate-600">
                    {new Date(ticket.createdAt).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Son Mesaj</span>
                  <span className="text-slate-600">
                    {new Date(ticket.lastMessageAt).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                {ticket.resolvedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Çözüm Tarihi</span>
                    <span className="text-slate-600">
                      {new Date(ticket.resolvedAt).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Hızlı İşlemler</h3>
              <div className="space-y-2">
                {ticket.status === 'open' && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleStatusChange('in_progress')}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    İşleme Al
                  </Button>
                )}
                {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleStatusChange('resolved')}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Çözüldü Olarak İşaretle
                  </Button>
                )}
                {ticket.status !== 'closed' && (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleStatusChange('closed')}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Ticket'ı Kapat
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Ticket'ı Sil
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ticket'ı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              #{ticket.ticketNumber} numaralı ticket'ı silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz ve tüm mesajlar silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Siliniyor...' : 'Sil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
